import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Course } from '../../types/course';
import SuggestedCourseCard from './SuggestedCourseCard';
import HomeSectionFrame from './HomeSectionFrame';

interface SuggestedCoursesSectionProps {
  courses: Course[];
  loading?: boolean;
}

const SuggestedCoursesSection: React.FC<SuggestedCoursesSectionProps> = ({
  courses,
  loading = false,
}) => {
  if (!loading && courses.length === 0) return null;

  return (
    <HomeSectionFrame
      id="suggested-courses-section"
      aria-labelledby="suggested-courses-heading"
      tone="paper"
    >
      <div className="mb-7 flex flex-col gap-5 lg:mb-9 lg:flex-row lg:items-end lg:justify-between lg:gap-10">
        <div className="max-w-xl space-y-2.5">
          <div className="flex items-center gap-2.5">
            <span className="inline-block h-5 w-1 shrink-0 rounded-full bg-brand-500" aria-hidden />
            <p className="text-sm font-semibold text-brand-600">كورسات مختارة</p>
          </div>
          <h2
            id="suggested-courses-heading"
            className="text-[clamp(1.6rem,1.5vw+1.1rem,2.35rem)] font-extrabold leading-snug text-slate-900"
          >
            رشحنالك أهم الكورسات
          </h2>
          <p className="max-w-[40ch] text-sm leading-relaxed text-slate-600 sm:text-base sm:leading-7">
            جمعنالك أهم وأشهر الكورسات هنا — ادخل وشوف التفاصيل واشترك بسهولة.
          </p>
        </div>
        <Link
          to="/courses/grade/secondary1"
          state={{ grade: 'Secondary1' }}
          className="inline-flex min-h-[46px] w-full shrink-0 items-center justify-center gap-2 rounded-xl border-2 border-brand-300 bg-white px-5 py-2.5 text-sm font-semibold text-brand-700 transition hover:-translate-y-px hover:border-brand-500 hover:bg-brand-50 sm:w-auto sm:text-base"
        >
          <span>المزيد من الكورسات</span>
          <ArrowLeft className="h-[18px] w-[18px]" aria-hidden />
        </Link>
      </div>

      {loading ? (
        <div className="flex gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="w-[min(86vw,20rem)] shrink-0 animate-pulse overflow-hidden rounded-xl border-2 border-brand-100 bg-white sm:w-[22rem]"
            >
              <div className="aspect-[1000/666] bg-brand-50" />
              <div className="space-y-2 p-4">
                <div className="h-5 w-3/4 rounded bg-brand-50" />
                <div className="h-4 w-full rounded bg-brand-50" />
                <div className="mt-2 h-10 w-full rounded bg-brand-50" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className="flex gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="list"
          aria-label="كورسات مرشّحة"
        >
          {courses.map((course) => (
            <div key={course.id} role="listitem" className="w-[min(86vw,20rem)] shrink-0 sm:w-[22rem]">
              <SuggestedCourseCard course={course} compact />
            </div>
          ))}
        </div>
      )}
    </HomeSectionFrame>
  );
};

export default SuggestedCoursesSection;
